import { versionForSha1, type GameVersion } from "@redchain/contracts";
import { apiBase } from "../env";
import { allRoms, putRom, romForVersion, type RomRecord } from "./db";

export type RomCheck =
  | { ok: true; rom: RomRecord }
  | { ok: false; reason: "unknown_rom" }
  | { ok: false; reason: "wrong_version"; detected: GameVersion; required: GameVersion };

export async function sha1Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-1", bytes as BufferSource);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Accepts only the Red or Blue ROM by SHA-1 (§7.1). If the player's game generation is
 * locked to one version, the other is refused with a prompt for the right one. Accepted
 * ROMs are kept in the origin-private IndexedDB `roms` store and never uploaded.
 */
export async function acceptRom(bytes: Uint8Array, lockedVersion: GameVersion | null): Promise<RomCheck> {
  const sha1 = await sha1Hex(bytes);
  const version = versionForSha1(sha1);
  if (!version) return { ok: false, reason: "unknown_rom" };
  if (lockedVersion && version !== lockedVersion) return { ok: false, reason: "wrong_version", detected: version, required: lockedVersion };
  const rom: RomRecord = { sha1, version, bytes, addedAt: Date.now() };
  await putRom(rom);
  return { ok: true, rom };
}

export async function acceptRomFile(file: File, lockedVersion: GameVersion | null): Promise<RomCheck> {
  return acceptRom(new Uint8Array(await file.arrayBuffer()), lockedVersion);
}

/** A previously stored ROM for this player's version (or any accepted ROM if unlocked). */
export async function storedRom(lockedVersion: GameVersion | null): Promise<RomRecord | null> {
  const found = lockedVersion ? ((await romForVersion(lockedVersion)) ?? null) : (await allRoms()).sort((a, b) => b.addedAt - a.addedAt)[0] ?? null;
  return found ?? hostedRom(lockedVersion ?? "RED");
}

/** The ROM the website serves (`GET /v1/rom/:version`), checked by SHA-1 and stored like a picked file. */
async function hostedRom(version: GameVersion): Promise<RomRecord | null> {
  try {
    const res = await fetch(`${apiBase}/rom/${version}`);
    if (!res.ok) return null;
    const check = await acceptRom(new Uint8Array(await res.arrayBuffer()), version);
    return check.ok ? check.rom : null;
  } catch {
    return null;
  }
}

export function versionLabel(v: GameVersion): string {
  return v === "RED" ? "Pokémon Red" : "Pokémon Blue";
}
